import { Button, ButtonType } from '@ardoq/button';
import {
  Color,
  colors,
  numericSpace,
  radius,
  shadows,
  space,
} from '@ardoq/design-tokens';
import { DropdownMenu2026 } from '@ardoq/dropdown-2026';
import { Icon, IconName, MinimalIconProps } from '@ardoq/icons';
import { FlexBox } from '@ardoq/layout';
import { Tooltip, useDelayedHover } from '@ardoq/popovers';
import { Text } from '@ardoq/typography';
import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import styled, { css } from 'styled-components';
import {
  DEFAULT_FLOATING_ACTION_BAR_LABELS,
  FloatingActionBarLabels,
} from '../labels';

export type FloatingActionBarAction = {
  label: string;
  onClick: VoidFunction;
  icon?: MinimalIconProps;
  color?: Color;
};

export type DisabledFloatingActionBarAction = {
  isDisabled: true;
  tooltip: string;
  label: string;
  icon?: MinimalIconProps;
};

export type FloatingActionBarActionItem =
  | FloatingActionBarAction
  | DisabledFloatingActionBarAction;

const isDisabledAction = (
  action: FloatingActionBarActionItem
): action is DisabledFloatingActionBarAction => 'isDisabled' in action;

export type FloatingActionBarProps = {
  selectedCount: number;
  entityName: string;
  actions: FloatingActionBarActionItem[];
  onCancel: VoidFunction;
  /** Ref to the element the bar should be anchored to (typically the table container) */
  anchorRef: React.RefObject<HTMLElement | null>;
  labels?: Partial<FloatingActionBarLabels>;
};

export const FloatingActionBar = ({
  selectedCount,
  entityName,
  actions,
  onCancel,
  anchorRef,
  labels: labelsOverride,
}: FloatingActionBarProps) => {
  const countRef = useRef<HTMLDivElement>(null);
  const cancelRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);

  const labels = { ...DEFAULT_FLOATING_ACTION_BAR_LABELS, ...labelsOverride };

  const { barRef, callbackRef } = useTopLayerPopover();

  const visibleCount = useActionOverflow({
    measureRef,
    countRef,
    cancelRef,
    anchorRef,
    actions,
    selectedCount,
    entityName,
  });

  useAnchorPositioning(barRef, anchorRef, visibleCount);

  const visibleActions = actions.slice(0, visibleCount);
  const overflowActions = actions.slice(visibleCount);

  return (
    <Bar
      ref={callbackRef}
      popover="manual"
      role="toolbar"
      aria-label={labels.actionsForSelectedItems}
    >
      <MeasureContainer ref={measureRef} aria-hidden="true">
        {actions.map(action => (
          <Button
            key={action.label}
            buttonType={ButtonType.GHOST}
            tabIndex={-1}
          >
            {action.icon && (
              <Icon iconName={action.icon.name} color={action.icon.color} />
            )}
            {action.label}
          </Button>
        ))}
      </MeasureContainer>

      <div ref={countRef}>
        <Text color="textSubtle">
          {labels.formatSelectedCount({ selectedCount, entityName })}
        </Text>
      </div>

      <FlexBox gap="xsmall" align="center">
        {visibleActions.map(action => (
          <ActionButton key={action.label} action={action} />
        ))}
        {overflowActions.length > 0 && (
          <OverflowMenu
            actions={overflowActions}
            moreActionsLabel={labels.moreActions}
          />
        )}
      </FlexBox>

      <div ref={cancelRef}>
        <Button buttonType={ButtonType.TERTIARY} onClick={onCancel}>
          {labels.cancel}
        </Button>
      </div>
    </Bar>
  );
};

const ActionButton = ({ action }: { action: FloatingActionBarActionItem }) => {
  if (isDisabledAction(action)) {
    return <DisabledActionButton action={action} />;
  }

  const tint = action.color ? colors[action.color] : undefined;
  return (
    <ActionButtonStyle
      buttonType={ButtonType.GHOST}
      onClick={action.onClick}
      $tint={tint}
    >
      {action.icon && (
        <Icon iconName={action.icon.name} color={tint ?? action.icon.color} />
      )}
      {action.label}
    </ActionButtonStyle>
  );
};

const DisabledActionButton = ({
  action,
}: {
  action: DisabledFloatingActionBarAction;
}) => {
  const anchorRef = useRef<HTMLSpanElement>(null);
  const { isOpen, handleMouseEnter, handleMouseLeave, handleClose } =
    useDelayedHover();

  return (
    <TooltipAnchor
      ref={anchorRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Button buttonType={ButtonType.GHOST} isDisabled>
        {action.icon && (
          <Icon iconName={action.icon.name} color={action.icon.color} />
        )}
        {action.label}
      </Button>
      <Tooltip isOpen={isOpen} onClose={handleClose} anchorRef={anchorRef}>
        {action.tooltip}
      </Tooltip>
    </TooltipAnchor>
  );
};

const OverflowMenu = ({
  actions,
  moreActionsLabel,
}: {
  actions: FloatingActionBarActionItem[];
  moreActionsLabel: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverId = useId();

  return (
    <>
      <Button
        ref={triggerRef}
        buttonType={ButtonType.GHOST}
        onClick={() => setIsOpen(open => !open)}
        aria-label={moreActionsLabel}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-controls={popoverId}
      >
        <Icon iconName={IconName.MORE_VERT} />
      </Button>
      <DropdownMenu2026
        anchorRef={triggerRef}
        content={{
          type: 'actions',
          items: actions.map(action =>
            isDisabledAction(action)
              ? {
                  type: 'button',
                  label: action.label,
                  icon: action.icon,
                  isDisabled: true,
                  tooltip: action.tooltip,
                  onClick: () => {},
                }
              : {
                  type: 'button',
                  label: action.label,
                  icon: action.icon,
                  onClick: () => {
                    action.onClick();
                    setIsOpen(false);
                  },
                }
          ),
        }}
        id={popoverId}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
      />
    </>
  );
};

const KEBAB_BUTTON_WIDTH = 40;
const BAR_MAX_WIDTH = 600;
const BOTTOM_INSET = numericSpace.medium;

const calculateVisibleCount = (
  barMaxWidth: number,
  countWidth: number,
  cancelWidth: number,
  buttonWidths: number[]
): number => {
  const barPadding = numericSpace.medium * 2;
  const barGap = numericSpace.medium;
  const actionGap = numericSpace.xsmall;

  const availableWidth =
    barMaxWidth - barPadding - countWidth - cancelWidth - barGap * 2;

  const totalButtonsWidth = buttonWidths.reduce(
    (sum, w, i) => sum + w + (i > 0 ? actionGap : 0),
    0
  );

  if (totalButtonsWidth <= availableWidth) {
    return buttonWidths.length;
  }

  let used = 0;
  let count = 0;
  for (let i = 0; i < buttonWidths.length; i++) {
    const gap = i > 0 ? actionGap : 0;
    const needed = gap + buttonWidths[i];
    if (used + needed + actionGap + KEBAB_BUTTON_WIDTH <= availableWidth) {
      used += needed;
      count++;
    } else {
      break;
    }
  }

  return count;
};

/**
 * Manages showing/hiding a popover element in the top layer.
 * Returns a callback ref that shows the popover when the DOM node mounts,
 * and cleans up on unmount.
 */
const useTopLayerPopover = () => {
  const barRef = useRef<HTMLDivElement>(null);

  const callbackRef = useCallback((node: HTMLDivElement | null) => {
    barRef.current = node;
    if (node && !node.matches(':popover-open')) {
      node.showPopover();
    }
  }, []);

  useEffect(() => {
    return () => {
      const bar = barRef.current;
      if (bar?.matches(':popover-open')) {
        bar.hidePopover();
      }
    };
  }, []);

  return { barRef, callbackRef };
};

/**
 * Measures action button widths from a hidden container and calculates
 * how many fit within the available space, accounting for the anchor width.
 */
const useActionOverflow = ({
  measureRef,
  countRef,
  cancelRef,
  anchorRef,
  actions,
  selectedCount,
  entityName,
}: {
  measureRef: React.RefObject<HTMLDivElement | null>;
  countRef: React.RefObject<HTMLDivElement | null>;
  cancelRef: React.RefObject<HTMLDivElement | null>;
  anchorRef: React.RefObject<HTMLElement | null>;
  actions: FloatingActionBarActionItem[];
  selectedCount: number;
  entityName: string;
}): number => {
  const [visibleCount, setVisibleCount] = useState(actions.length);

  const recalculate = useCallback(() => {
    const countEl = countRef.current;
    const cancelEl = cancelRef.current;
    const measureEl = measureRef.current;
    const anchor = anchorRef.current;
    if (!countEl || !cancelEl || !measureEl) return;

    const anchorWidth = anchor?.getBoundingClientRect().width ?? BAR_MAX_WIDTH;
    const effectiveMaxWidth = Math.min(BAR_MAX_WIDTH, anchorWidth);

    const countWidth = countEl.offsetWidth;
    const cancelWidth = cancelEl.offsetWidth;
    const buttonWidths = Array.from(measureEl.children).map(
      child => (child as HTMLElement).offsetWidth
    );

    setVisibleCount(
      calculateVisibleCount(
        effectiveMaxWidth,
        countWidth,
        cancelWidth,
        buttonWidths
      )
    );
  }, [anchorRef, measureRef, countRef, cancelRef]);

  useLayoutEffect(recalculate, [
    recalculate,
    actions,
    selectedCount,
    entityName,
  ]);

  useEffect(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;

    const observer = new ResizeObserver(recalculate);
    observer.observe(anchor);

    return () => observer.disconnect();
  }, [anchorRef, recalculate]);

  return visibleCount;
};

/**
 * Finds the bottom edge of the last visible row in an AG Grid container.
 * Returns null if no rows are found.
 */
const getLastRowBottom = (anchor: HTMLElement): number | null => {
  const rows = anchor.querySelectorAll('.ag-row');
  if (rows.length === 0) return null;

  let maxBottom = -Infinity;
  for (const row of rows) {
    const bottom = row.getBoundingClientRect().bottom;
    if (bottom > maxBottom) {
      maxBottom = bottom;
    }
  }

  return maxBottom;
};

/**
 * Positions a fixed-position element at the bottom center of an anchor element.
 * When the table content doesn't fill the container, positions just below the
 * last row instead of at the container edge. When the table extends past the
 * bottom of the viewport, sticks the element to the viewport bottom so it stays
 * visible.
 * Tracks anchor movement via ResizeObserver and scroll events.
 */
const useAnchorPositioning = (
  barRef: React.RefObject<HTMLDivElement | null>,
  anchorRef: React.RefObject<HTMLElement | null>,
  visibleCount: number
) => {
  useLayoutEffect(() => {
    const bar = barRef.current;
    const anchor = anchorRef.current;
    if (!bar || !anchor) return;

    const updatePosition = () => {
      const anchorRect = anchor.getBoundingClientRect();
      const barRect = bar.getBoundingClientRect();
      const lastRowBottom = getLastRowBottom(anchor);

      const insetFromBottom = anchorRect.bottom - barRect.height - BOTTOM_INSET;
      const belowLastRow =
        lastRowBottom !== null ? lastRowBottom + BOTTOM_INSET : insetFromBottom;
      const stuckToViewportBottom =
        window.innerHeight - barRect.height - BOTTOM_INSET;

      const top = Math.min(
        belowLastRow,
        insetFromBottom,
        stuckToViewportBottom
      );
      const left = anchorRect.left + anchorRect.width / 2 - barRect.width / 2;

      bar.style.left = `${left}px`;
      bar.style.top = `${top}px`;
    };

    updatePosition();

    const observer = new ResizeObserver(updatePosition);
    observer.observe(anchor);
    observer.observe(bar);

    window.addEventListener('resize', updatePosition);
    document.addEventListener('scroll', updatePosition, true);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updatePosition);
      document.removeEventListener('scroll', updatePosition, true);
    };
  }, [barRef, anchorRef, visibleCount]);
};

const TooltipAnchor = styled.span`
  display: inline-flex;
`;

const ActionButtonStyle = styled(Button)<{ $tint?: string }>`
  ${({ $tint }) =>
    $tint &&
    css`
      &,
      &:hover {
        color: ${$tint};
      }
    `}
`;

const MeasureContainer = styled.div`
  position: absolute;
  visibility: hidden;
  pointer-events: none;
  display: flex;
  gap: ${space.xsmall};
`;

const Bar = styled.div`
  /* Reset popover UA styles */
  margin: 0;
  inset: auto;
  overflow: visible;
  padding: ${space.small} ${space.medium};

  position: fixed;
  display: flex;
  align-items: center;
  gap: ${space.medium};
  max-width: ${BAR_MAX_WIDTH}px;
  background: ${colors.surfaceDefault};
  border-radius: ${radius.r8};
  box-shadow: ${shadows.shadowL};
  border: 1px solid ${colors.borderSubtle};
  white-space: nowrap;
`;
